import { useState, useEffect, useCallback } from 'react';
import { Download, History as HistoryIcon, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Lightbox } from '@/components/ui/Lightbox';
import { fetchImageHistory, type ImageHistoryEntry } from '@/services/adminImageHistoryService';

interface ImageHistoryDrawerProps {
  imageId: string | null;
  roomType: string | null;
  onClose: () => void;
}

function fmtRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'gerade eben';
  if (min < 60) return `vor ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `vor ${h} h`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'gestern';
  if (d < 7) return `vor ${d} Tagen`;
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtFilename(roomType: string | null, versionType: string, createdAt: string): string {
  const ts = new Date(createdAt)
    .toISOString()
    .replace(/[:T]/g, '')
    .replace(/\..+/, '')
    .slice(0, 15);
  const safe = (s: string) =>
    s.toLowerCase().replace(/[^a-z0-9_-]+/g, '_').replace(/^_+|_+$/g, '') || 'image';
  return `${safe(roomType || 'image')}_${safe(versionType)}_${ts}.jpg`;
}

export const ImageHistoryDrawer: React.FC<ImageHistoryDrawerProps> = ({ imageId, roomType, onClose }) => {
  const [entries, setEntries] = useState<ImageHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [lightboxFilename, setLightboxFilename] = useState<string | null>(null);
  const [brokenIds, setBrokenIds] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    if (!imageId) {
      setEntries([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    setBrokenIds(new Set());
    try {
      const rows = await fetchImageHistory(imageId);
      setEntries(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler beim Laden');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [imageId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (imageId === null) {
      setLightboxUrl(null);
      setLightboxFilename(null);
    }
  }, [imageId]);

  const handleDownload = (entry: ImageHistoryEntry) => {
    const a = document.createElement('a');
    a.href = entry.publicUrl;
    a.download = fmtFilename(roomType, entry.version_type, entry.created_at);
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleBroken = (id: string) => {
    console.warn('[ImageHistoryDrawer] broken image url for entry', id);
    setBrokenIds(prev => new Set(prev).add(id));
  };

  const isOpen = imageId !== null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        {/* Scrolling lives in an inner child: making the transformed (translate-centered),
            rounded DialogContent itself the scroller causes white compositing artifacts
            at the edges during fast momentum scrolls (same pattern as UpdateDetailModal). */}
        <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="shrink-0 px-6 pt-6 pb-4">
            <DialogTitle className="flex items-center gap-2 pr-8">
              <HistoryIcon className="w-5 h-5 text-primary" />
              Historie {roomType ? `· ${roomType}` : ''}
            </DialogTitle>
          </DialogHeader>

          <div className="min-h-0 overflow-y-auto overscroll-contain px-6 pb-6 space-y-4">
            {error && (
              <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-destructive text-sm">
                {error}
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : entries.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                Keine Historie verfügbar für dieses Bild.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {entries.map((entry) => {
                  const broken = brokenIds.has(entry.id);
                  return (
                    <div key={entry.id} className="space-y-2">
                      <div className="relative group">
                        {broken ? (
                          <div className="w-full aspect-[4/3] rounded-lg border border-border bg-muted flex items-center justify-center text-xs text-muted-foreground">
                            Bild nicht verfügbar
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setLightboxUrl(entry.publicUrl);
                              setLightboxFilename(fmtFilename(roomType, entry.version_type, entry.created_at));
                            }}
                            className="block w-full"
                          >
                            <img
                              src={entry.publicUrl}
                              alt={entry.version_type}
                              onError={() => handleBroken(entry.id)}
                              className="w-full aspect-[4/3] object-cover rounded-lg border border-border hover:border-primary/50 transition-colors"
                            />
                          </button>
                        )}
                        <button
                          onClick={() => handleDownload(entry)}
                          className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-primary text-white rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Herunterladen"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className={`px-2 py-0.5 rounded font-medium ${
                          entry.version_type === 'Staged'
                            ? 'bg-emerald-500/10 text-emerald-600'
                            : entry.version_type === 'Empty'
                            ? 'bg-amber-500/10 text-amber-600'
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          {entry.version_type}
                        </span>
                        <span className="text-muted-foreground">{fmtRelative(entry.created_at)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {lightboxUrl && (
        <Lightbox
          url={lightboxUrl}
          onClose={() => { setLightboxUrl(null); setLightboxFilename(null); }}
          onDownload={() => {
            if (!lightboxUrl || !lightboxFilename) return;
            const a = document.createElement('a');
            a.href = lightboxUrl;
            a.download = lightboxFilename;
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            document.body.appendChild(a);
            a.click();
            a.remove();
          }}
        />
      )}
    </>
  );
};
